import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import StudentLayout from './components/StudentLayout';
import api from '../api/api';

const StudentAnnouncements = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let isMounted = true;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get('/notifications');
        if (!isMounted) return;
        setItems(Array.isArray(data) ? data : Array.isArray(data?.items) ? data.items : []);
      } finally {
        if (isMounted) setLoading(false);
      }
    })();
    return () => {
      isMounted = false;
    };
  }, []);

  const announcements = useMemo(
    () =>
      items
        .slice()
        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()),
    [items]
  );

  const formatDateTime = (value) => {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleString();
  };

  return (
    <StudentLayout>
      <Box>
        <Stack spacing={3}>
          <Box>
            <Typography variant="h4" fontWeight={800} color="primary.main">
              Announcements
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Stay informed about important updates from the Apex team.
            </Typography>
          </Box>

          <Card variant="outlined">
            <CardContent>
              {loading ? (
                <Stack alignItems="center" spacing={2} py={4}>
                  <CircularProgress size={32} />
                  <Typography variant="body2" color="text.secondary">
                    Loading announcements...
                  </Typography>
                </Stack>
              ) : announcements.length === 0 ? (
                <Alert severity="info" variant="outlined" sx={{ borderRadius: 2 }}>
                  There are no announcements right now. Check back soon!
                </Alert>
              ) : (
                <List disablePadding>
                  {announcements.map((announcement, index) => (
                    <React.Fragment key={announcement._id || announcement.createdAt || index}>
                      <ListItem alignItems="flex-start" disableGutters>
                        <ListItemText
                          primary={
                            <Typography variant="h6" fontWeight={700} color="primary.main">
                              {announcement.title || 'Announcement'}
                            </Typography>
                          }
                          secondary={
                            <Stack spacing={1}>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ fontWeight: 600, letterSpacing: 0.2 }}
                              >
                                {formatDateTime(announcement.createdAt)}
                              </Typography>
                              <Typography
                                variant="body2"
                                color="text.primary"
                                sx={{ whiteSpace: 'pre-wrap' }}
                              >
                                {announcement.body || announcement.message || 'No details were provided.'}
                              </Typography>
                            </Stack>
                          }
                        />
                      </ListItem>
                      {index !== announcements.length - 1 && <Divider component="li" sx={{ my: 2 }} />}
                    </React.Fragment>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        </Stack>
      </Box>
    </StudentLayout>
  );
};

export default StudentAnnouncements;
